import { z } from 'zod/v4';

import { FeatureFlagNameRuntype } from '../featureFlagNameBackendTypes';
import { endpoint } from '../http/endpoint';
import { HttpEndpoint } from '../http/HttpEndpoint';
import { PatchPaymentCredentialsRuntype, PaymentCredentialsRuntype } from '../orders';
import { PreferenceStatusEnumRuntype } from '../part';
import { FindSitesResponseRuntype, SiteRuntype } from './InventorySiteBackendTypes';
import {
    LandingPageSettingsRuntype,
    LandingPageSettingsUpdateRuntype,
    LandingPageTileInsertRuntype,
    LandingPageTileRuntype,
    LandingPageTilesResponseRuntype,
    PublicLandingPageResponseRuntype,
} from './landingPageSettingsBackendTypes';
import {
    FeatureFlagConfigsResponseRuntype,
    FeatureFlagRequestRuntype,
    FeatureFlagUserOverridesRequestRuntype,
    GlobalCurrencySettingsDTORuntype,
    ManufacturerPreferenceDTORuntype,
    ManufacturerPreferenceInputRuntype,
    ManufacturerPreferenceUpdateRuntype,
    OrganizationSettingsDTORuntype,
    PatchOrganizationSettingsDTORuntype,
    PatchSelfSignupSettingsRuntype,
    PublicExchangeRateSummaryDTORuntype,
    SelfSignupSettingsRuntype,
} from './organizationSettingsBackendTypes';
import { PcbApiSettingsDTORuntype } from './pcbApiSettingsBackendTypes';

const sitesDependentQueries: HttpEndpoint[] = [
    'GET /organization-settings/sites',
    'GET /resources',
    'GET /resources/:resourceId',
    'GET /expenses',
    'GET /expenses/:expenseId',
    'GET /activities',
    'GET /activities/:activityId',
];

export const organizationSettingsEndpoints = {
    'GET /organization-settings/organization-currency-settings': endpoint({
        description: 'Returns the current selected currency as well as other currency related information',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: GlobalCurrencySettingsDTORuntype }),
    }),

    'PATCH /organization-settings/organization-currency-settings': endpoint({
        description: 'Updates current selected currency as well as manual exchange rates.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: GlobalCurrencySettingsDTORuntype,
        responseBody: z.object({ data: GlobalCurrencySettingsDTORuntype }),
        actionPermission: () => 'CanUpdateOrganizationCurrencySettings',
    }),

    'GET /public-exchange-rate': endpoint({
        description: 'Returns the public exchange rate for the given currency',
        pathParams: z.undefined(),
        queryParams: z.object({ currency: z.string() }),
        requestBody: z.undefined(),
        responseBody: z.object({ data: PublicExchangeRateSummaryDTORuntype }),
    }),

    'GET /organization-settings/webhooks/dashboard-url': endpoint({
        description: 'Get a url to the svix app portal.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        responseBody: z.string(),
        requestBody: z.undefined(),
        actionPermission: () => 'CanGetWebhooksDashboardUrl',
    }),

    'GET /organization-settings/payments/credentials': endpoint({
        description: 'Get the payment credentials for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        responseBody: PaymentCredentialsRuntype,
        requestBody: z.undefined(),
        actionPermission: () => 'CanViewPaymentCredentials',
    }),

    'PATCH /organization-settings/payments/credentials': endpoint({
        description: 'Update the payment credentials for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchPaymentCredentialsRuntype,
        responseBody: PaymentCredentialsRuntype,
        invalidates: ['GET /organization-settings/payments/credentials'],
        actionPermission: () => 'CanUpdatePaymentCredentials',
    }),

    'GET /organization-settings/sites': endpoint({
        description: 'Returns the sites of the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            data: z.object({
                sites: z.array(SiteRuntype),
            }),
        }),
    }),
    'POST /organization-settings/sites/find': endpoint({
        description: 'Finds organization sites by name or site number',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            queries: z.array(z.string()),
        }),
        responseBody: FindSitesResponseRuntype,
    }),
    'POST /organization-settings/sites': endpoint({
        description: 'Updates the sites of an organization.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: SiteRuntype.omit({ id: true }),
        responseBody: z.object({ data: SiteRuntype }),
        invalidates: sitesDependentQueries,
        actionPermission: () => 'CanCreateSite',
    }),
    'PATCH /organization-settings/sites/:siteId': endpoint({
        description: 'Updates the sites of an organization.',
        pathParams: z.object({ siteId: z.string() }),
        queryParams: z.undefined(),
        requestBody: SiteRuntype.omit({ id: true }),
        responseBody: z.object({ data: SiteRuntype }),
        invalidates: sitesDependentQueries,
        actionPermission: () => 'CanUpdateSite',
    }),
    'DELETE /organization-settings/sites/:siteId': endpoint({
        description: 'Delete the site of an organization.',
        pathParams: z.object({ siteId: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ deleted: z.number() }),
        invalidates: sitesDependentQueries,
        actionPermission: () => 'CanDeleteSite',
    }),
    'GET /organization-settings/self-signup': endpoint({
        description: 'Get the self-signup settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: SelfSignupSettingsRuntype,
    }),
    'PATCH /organization-settings/self-signup': endpoint({
        description: 'Update the self-signup settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchSelfSignupSettingsRuntype,
        responseBody: SelfSignupSettingsRuntype,
        invalidates: ['GET /organization-settings/self-signup'],
    }),

    // Manufacturer Preferences
    'GET /organization-settings/manufacturer-preferences': endpoint({
        description: 'Get the manufacturer preferences for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({
            items: z.array(ManufacturerPreferenceDTORuntype),
        }),
    }),
    'POST /organization-settings/manufacturer-preferences': endpoint({
        description: 'Add a manufacturer preference for a part category',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: ManufacturerPreferenceInputRuntype,
        responseBody: ManufacturerPreferenceDTORuntype,
        invalidates: ['GET /organization-settings/manufacturer-preferences'],
        actionPermission: () => 'CanCreateManufacturerPreference',
    }),

    'PATCH /organization-settings/manufacturer-preferences/:id/:status': endpoint({
        description: 'Updates the preferred manufacturer preference',
        pathParams: z.object({ id: z.number(), status: PreferenceStatusEnumRuntype }),
        queryParams: z.undefined(),
        requestBody: ManufacturerPreferenceUpdateRuntype,
        responseBody: ManufacturerPreferenceDTORuntype,
        invalidates: ['GET /organization-settings/manufacturer-preferences'],
        actionPermission: () => 'CanUpdateManufacturerPreference',
    }),

    'DELETE /organization-settings/manufacturer-preferences/:partCategoryId/:status': endpoint({
        description: 'Delete the manufacturer preferences for a part category',
        pathParams: z.object({ partCategoryId: z.number(), status: PreferenceStatusEnumRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string(),
        invalidates: ['GET /organization-settings/manufacturer-preferences'],
        actionPermission: () => 'CanDeleteManufacturerPreference',
    }),

    'GET /organization-settings/auto-rfq-number/next': endpoint({
        description: 'Get the auto-generated RfQ number for the next RfQ, if exists',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.string().nullable(),
    }),

    'GET /organization-settings/checkout-conditions': endpoint({
        description: 'Get the checkout conditions for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(
            z.object({
                translation_en: z.string(),
                translation_de: z.string(),
                translation_fr: z.string(),
                translation_it: z.string(),
                translation_es: z.string(),
                rank: z.number(),
                created_at: z.string(),
            }),
        ),
    }),

    'POST /organization-settings/checkout-conditions/replace-all': endpoint({
        description: 'Deletes all previous checkout conditions and creates new ones',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.array(
            z.object({
                translation_en: z.string().nullable(),
                translation_de: z.string().nullable(),
                translation_fr: z.string().nullable(),
                translation_it: z.string().nullable(),
                translation_es: z.string().nullable(),
                rank: z.number().nullable(),
            }),
        ),
        responseBody: z.array(
            z.object({
                translation_en: z.string().nullable(),
                translation_de: z.string().nullable(),
                translation_fr: z.string().nullable(),
                translation_it: z.string().nullable(),
                translation_es: z.string().nullable(),
                rank: z.number().nullable(),
                created_at: z.string(),
            }),
        ),
        invalidates: ['GET /organization-settings/checkout-conditions'],
        actionPermission: () => 'CanEditCheckoutConditions',
    }),

    'PATCH /organization-settings/feature-flags': endpoint({
        description: 'Toggle a feature flag for the organisation',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: FeatureFlagRequestRuntype,
        responseBody: z.undefined().or(z.null()),
        invalidates: ['GET /organization-settings/feature-flags/config', 'GET /my/feature-flags'],
    }),

    'GET /organization-settings/feature-flags/config': endpoint({
        description: 'Get the full feature flag config (tenant value + per-user overrides)',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: FeatureFlagConfigsResponseRuntype,
    }),

    'PATCH /organization-settings/feature-flags/user-overrides': endpoint({
        description: 'Set the per-user overrides for a feature flag',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: FeatureFlagUserOverridesRequestRuntype,
        responseBody: z.undefined().or(z.null()),
        invalidates: ['GET /organization-settings/feature-flags/config', 'GET /my/feature-flags'],
    }),

    'GET /feature-flags/:featureFlag/tenants': endpoint({
        description: 'List the tenants that have a feature flag on',
        pathParams: z.object({ featureFlag: FeatureFlagNameRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ tenants: z.array(z.string()) }),
        base: 'internal',
    }),

    'GET /organization-settings/landing-page-tiles': endpoint({
        description: 'Get the landing page tiles and settings for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: LandingPageTilesResponseRuntype,
    }),

    'GET /landing-page-tiles': endpoint({
        description:
            'Get the landing page tiles and settings for the organization (public endpoint, no authentication required)',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PublicLandingPageResponseRuntype,
        base: 'public',
    }),

    'GET /organization-settings/landing-page-settings': endpoint({
        description: 'Get the landing page settings for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: LandingPageSettingsRuntype,
        invalidates: [],
    }),

    'PATCH /organization-settings/landing-page-settings': endpoint({
        description: 'Update the landing page settings for the organization',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: LandingPageSettingsUpdateRuntype,
        responseBody: LandingPageSettingsRuntype,
        invalidates: ['GET /organization-settings/landing-page-settings'],
        actionPermission: () => 'CanUpdateLandingPageSettings',
    }),

    'POST /organization-settings/landing-page-tiles/replace-all': endpoint({
        description: 'Deletes all previous landing page tiles and creates new ones',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.array(LandingPageTileInsertRuntype),
        responseBody: z.array(LandingPageTileRuntype),
        invalidates: ['GET /organization-settings/landing-page-tiles', 'GET /landing-page-tiles'],
        actionPermission: () => 'CanEditLandingPageTiles',
    }),

    'GET /organization-settings/pcb-api-settings': endpoint({
        description: 'Get the PCB API settings for excluding industries from PCB offer fetching',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: PcbApiSettingsDTORuntype }),
    }),

    'PATCH /organization-settings/pcb-api-settings': endpoint({
        description: 'Update the PCB API settings for excluding industries from PCB offer fetching',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PcbApiSettingsDTORuntype,
        responseBody: z.object({ data: PcbApiSettingsDTORuntype }),
        invalidates: ['GET /organization-settings/pcb-api-settings'],
    }),

    'GET /organization/settings': endpoint({
        description: 'Get all organization settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: OrganizationSettingsDTORuntype,
    }),

    'PATCH /organization/settings': endpoint({
        description: 'Update organization settings',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PatchOrganizationSettingsDTORuntype,
        responseBody: OrganizationSettingsDTORuntype,
        invalidates: ['GET /organization/settings'],
    }),
};
